import {useTranslation} from "react-i18next";
import {useSnackbar} from "notistack";
import {useFormik} from "formik";
import * as yup from "yup";
import {OrganizationAddStageRequest, useOrganizationAddStageMutation, useStageListQuery} from "../../../app/api/api";
import DefaultDialog, {DefaultDialogSize} from "../../../components/Dialog/ClosableDialog";
import {Button, FormControl, Stack} from "@mui/material";
import React from "react";
import {Stage, StageField} from "../../../app/api/model/stage";
import {Filter, FilterOperator} from "../../../components/ExTable";
import FormTextInputField from "../../../components/input/FormTextField";
import {getFormErrorText} from "../../../utils/getFormTextError";
import {AsyncListAutocomplete} from "../../../components/input/AsyncAutocomplete/variants";

interface OrganizationUpdateStageDialogProps {
    open: boolean;
    onClose: () => void;
    organizationId: number;
    currentStages?: string;
}

export default function OrganizationAddStageDialog({
    open,
    onClose,
    organizationId,
    currentStages,
}: OrganizationUpdateStageDialogProps) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [organizationAddStage] = useOrganizationAddStageMutation();

    const handleClose = () => {
        onClose?.();
        formik.resetForm();
    };

    const getValidationSchema = () =>
        yup.object({
            stage: yup.string().required(t("validation_inputRequired")),
            adminEmail: yup.string().email(t("validation_invalidEmail")).required(t("validation_inputRequired")),
        });

    const formik = useFormik({
        initialValues: {
            stage: "",
            adminEmail: "",
        },
        validationSchema: getValidationSchema(),
        onSubmit: async (values) => {
            const requestData = {
                organizationId: organizationId,
                stage: values.stage,
                adminEmail: values.adminEmail,
            } as OrganizationAddStageRequest;
            await organizationAddStage(requestData).unwrap();
            enqueueSnackbar(t("organization_stageAddedSuccessfully"), {variant: "success"});
            handleClose();
        },
    });

    function handleSubmit() {
        formik.handleSubmit();
    }

    const getAdditionalFilters = () => {
        const additionalFilters: Filter[] = [];
        if (currentStages !== "") {
            const stages = currentStages?.split(",");
            stages?.forEach((stage) => {
                additionalFilters.push({f: "identifier", o: FilterOperator.NOT, v: stage});
            });
        }

        return additionalFilters;
    };

    return (
        <DefaultDialog
            size={DefaultDialogSize.MEDIUM}
            open={open}
            titleKey={"action_organizationAddStage"}
            onClose={handleClose}
            actions={
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    disabled={formik.values.adminEmail === "" || formik.values.stage === ""}
                >
                    {t("action_save")}
                </Button>
            }
        >
            <form onSubmit={formik.handleSubmit}>
                <Stack spacing={2} pt={1}>
                    <AsyncListAutocomplete<Stage>
                        queryHook={useStageListQuery}
                        additionalFilters={getAdditionalFilters()}
                        idField={StageField.IDENTIFIER}
                        queryField={StageField.DESCRIPTION}
                        label={t("organizations_stage")}
                        value={formik.values.stage}
                        onChange={(value) => {
                            if (value) {
                                formik.setFieldValue("stage", value?.identifier);
                            }
                        }}
                    />
                    <FormControl fullWidth error={getFormErrorText(formik, ["adminEmail"])}>
                        <FormTextInputField
                            fieldName="adminEmail"
                            label={t("organizations_adminEmail")}
                            form={formik}
                        />
                    </FormControl>
                </Stack>
            </form>
        </DefaultDialog>
    );
}
